import type { Dict } from "mixpanel-browser";

const CONSENT_KEY = "mp_consent";

/**
 * Fired on window whenever consent is granted/denied so other analytics
 * (e.g. Google Analytics) can react without polling. See `GoogleAnalytics`.
 */
export const CONSENT_CHANGE_EVENT = "consent:change";

function emitConsentChange() {
  if (!isBrowser) return;
  globalThis.dispatchEvent(new Event(CONSENT_CHANGE_EVENT));
}

type Mixpanel = typeof import("mixpanel-browser").default;
let mp: Mixpanel | null = null;
let initPromise: Promise<Mixpanel | null> | null = null;
const queue: Array<(m: Mixpanel) => void> = [];

const isBrowser = typeof globalThis.window !== "undefined";

export function getConsent(): "granted" | "denied" | "unset" {
  if (!isBrowser) return "unset";
  const v = globalThis.localStorage.getItem(CONSENT_KEY);
  if (v === "granted" || v === "denied") return v;
  return "unset";
}

export function initMixpanel(): Promise<Mixpanel | null> {
  if (initPromise) return initPromise;
  if (!isBrowser) return Promise.resolve(null);

  const token = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;
  if (!token) {
    console.warn("[Mixpanel] NEXT_PUBLIC_MIXPANEL_TOKEN is not set.");
    return Promise.resolve(null);
  }

  if (getConsent() !== "granted") return Promise.resolve(null);

  const isProd = process.env.NODE_ENV === "production";

  initPromise = import("mixpanel-browser").then((mod) => {
    const instance = mod.default;
    instance.init(token, {
      debug: !isProd,
      api_host: `${globalThis.location.origin}/mp`,
      track_pageview: "full-url",
      persistence: "localStorage",
      autocapture: true,
      record_sessions_percent: isProd ? 100 : 0,
      record_heatmap_data: isProd,
      record_mask_text_selector: ".mp-mask, input, textarea",
      ignore_dnt: false,
      opt_out_tracking_by_default: false,
    });
    instance.opt_in_tracking();
    mp = instance;
    while (queue.length) queue.shift()!(instance);
    return instance;
  }).catch((err) => {
    console.error("[Mixpanel] Failed to init:", err);
    initPromise = null;
    return null;
  });

  return initPromise;
}

function withMp(fn: (m: Mixpanel) => void) {
  if (mp) { fn(mp); return; }
  queue.push(fn);
  initMixpanel();
}

export function grantConsent() {
  if (!isBrowser) return;
  globalThis.localStorage.setItem(CONSENT_KEY, "granted");
  initPromise = null;
  mp = null;
  initMixpanel();
  emitConsentChange();
}

export function denyConsent() {
  if (!isBrowser) return;
  globalThis.localStorage.setItem(CONSENT_KEY, "denied");
  withMp((m) => m.opt_out_tracking());
  emitConsentChange();
}

export function track(event: string, props?: Dict) {
  withMp((m) => m.track(event, props));
}

export function identify(userId: string, props?: Dict) {
  withMp((m) => {
    m.identify(userId);
    if (props) m.people.set(props);
  });
}

export function setPeople(props: Dict) {
  withMp((m) => m.people.set(props));
}

export function resetAnalytics() {
  withMp((m) => m.reset());
}
